/**
 * DemoModeSelector — Affiché juste après "Mode = register" dans Login.jsx.
 * Présente 2 cartes (Découverte vs Vierge) avant le formulaire d'inscription.
 * Le parent reçoit le mode choisi via onSelect(mode).
 */
import React from 'react';
import { Sparkles, Wand2, CheckCircle2, ArrowRight } from 'lucide-react';

const FEATURES_DISCOVERY = [
  '3 entreprises clientes + 12 stagiaires',
  '3 programmes complets (Bureautique, Management, Tech)',
  '5 sessions à différents stades',
  '4 devis et 3 factures (payée, en attente, en retard)',
  '2 conventions signées + émargements',
  'CRM avec 8 contacts et 5 deals en pipeline',
  '30 logs d\'audit étalés sur 15 jours',
];

const FEATURES_BLANK = [
  'Base 100% vide, prête pour vos vraies données',
  'Assistant pas-à-pas en 8 étapes guidées',
  'Configuration progressive : OF, branding, programmes…',
  'Aide contextuelle à chaque étape',
  'Possibilité de sauter et reprendre plus tard',
  'Templates Qualiopi optionnels',
  'Idéal pour tester avec vos propres données dès le départ',
];

export default function DemoModeSelector({ onSelect }) {
  return (
    <div data-testid="demo-mode-selector" className="space-y-4">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold text-slate-900">Comment voulez-vous découvrir OFManager ?</h2>
        <p className="text-sm text-slate-500 mt-1">Choisissez votre mode d'essai gratuit 15 jours</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Mode Découverte */}
        <button
          type="button"
          onClick={() => onSelect('discovery')}
          data-testid="demo-mode-discovery-card"
          className="group text-left p-5 rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white hover:border-indigo-500 hover:shadow-lg transition-all"
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            <span className="text-xs font-bold uppercase tracking-wide text-indigo-700">Recommandé</span>
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-1">Mode Découverte</h3>
          <p className="text-xs text-slate-600 mb-3">
            Données réalistes pré-remplies sur tous les modules. Idéal pour voir ce qu'OFManager fait vraiment.
          </p>
          <ul className="space-y-1.5 mb-4">
            {FEATURES_DISCOVERY.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px] text-slate-700">
                <CheckCircle2 className="w-3.5 h-3.5 text-indigo-500 mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 group-hover:gap-2 transition-all">
            Choisir ce mode <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </button>

        {/* Mode Vierge */}
        <button
          type="button"
          onClick={() => onSelect('blank')}
          data-testid="demo-mode-blank-card"
          className="group text-left p-5 rounded-xl border-2 border-slate-200 bg-white hover:border-slate-500 hover:shadow-lg transition-all"
        >
          <div className="flex items-center gap-2 mb-2">
            <Wand2 className="w-5 h-5 text-slate-700" />
            <span className="text-xs font-bold uppercase tracking-wide text-slate-600">Pour pros</span>
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-1">Mode Vierge + Assistant</h3>
          <p className="text-xs text-slate-600 mb-3">
            Base vide avec un assistant pas-à-pas pour configurer votre OF avec vos vraies données.
          </p>
          <ul className="space-y-1.5 mb-4">
            {FEATURES_BLANK.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px] text-slate-700">
                <CheckCircle2 className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 group-hover:gap-2 transition-all">
            Choisir ce mode <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </button>
      </div>

      <p className="text-center text-[11px] text-slate-400 mt-3">
        Vous pourrez à tout moment supprimer les données de démo et repartir d'une base vierge depuis les paramètres.
      </p>
    </div>
  );
}
